import { Button, EButtonColorVariant } from '@outdoorsyco/bonfire';
import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import Heading from '@/components/switchback/Heading/Heading';
import Link from '@/components/switchback/Link/Link';
import TextInput from '@/components/switchback/TextInput';
import { emailValidator } from '@/constants/validationRules';
import apiRequest from '@/services/apiRequest';
import { trackEvent } from '@/services/track-event';
import { getCoreApi } from '@/utility/getCoreApi';

interface IProps {
  /**
   * The title for the newsletter component.
   */

  title?: string;

  /**
   * The pageType to send in a trackEvent to know where the user is singing up for the newsletter.
   */

  source?: string;

  'data-testid'?: string;
}

type FormData = {
  email: string;
};

const NewsLetter: React.FC<IProps> = ({
  title = '',
  source = 'newsletter-signup',
  'data-testid': testId = 'newsletter',
}) => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();
  const [hasSignedUp, setHasSignedUp] = useState(false);
  const [hasAPIError, setHasAPIError] = useState(false);
  const buttonText = intl.formatMessage({ defaultMessage: 'Sign Up', id: '39AHJm' });

  const newsletterSignup = useCallback<(email: FormData['email']) => void>(
    async (email: string) => {
      const url = `${getCoreApi()}/email/signup`;
      const data = JSON.stringify({ email, source });

      await apiRequest({
        url,
        method: 'post',
        data,
      });
    },
    [source],
  );

  const onSubmit = useCallback<(data: FormData) => void>(
    async ({ email }) => {
      try {
        await newsletterSignup(email);
        trackSignup(source);
        setHasSignedUp(true);
      } catch {
        setHasAPIError(true);
      }
    },
    [newsletterSignup, source],
  );

  const dismissApiErrorMessage = () => {
    if (hasAPIError) {
      setHasAPIError(false);
    }
  };

  return (
    <div className="items-center grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-24">
      <Heading data-testid={testId} level={2} className="md:max-w-[250px] highlight autoType800 ">
        {title}
      </Heading>
      {hasSignedUp && (
        <>
          <div className="highlight autoType600">
            {intl.formatMessage({ defaultMessage: `You're all set.`, id: 'pw/qng' })}
          </div>
          <div className="text autoType400">
            {intl.formatMessage({
              defaultMessage:
                'Expect adventure guides, discount code, and adventure information in your email box.',
              id: 'xBRJL/',
            })}
          </div>
        </>
      )}
      {!hasSignedUp && (
        <div>
          {hasAPIError && (
            <div className="p-2 mb-4 text-red-800 bg-white border border-red-800 rounded text-200">
              {intl.formatMessage({
                defaultMessage: 'Opps, there was a error. Please try again.',
                id: 'NDvJu/',
              })}
            </div>
          )}
          <form
            className="lg:flex lg:flex-wrap lg:items-start"
            data-testid="email-form"
            onSubmit={handleSubmit(onSubmit)}>
            <div className="flex-1 lg:mr-4">
              <TextInput
                id="input-id"
                data-testid="newsletter-input"
                autoComplete="email"
                onFocus={dismissApiErrorMessage}
                label={intl.formatMessage({ defaultMessage: 'Email Address', id: 'xxQxLE' })}
                {...register('email', emailValidator)}
              />
              {errors.email && (
                <span className="text-sm text-red-800">
                  {errors.email.type === 'pattern' && errors.email.message}
                  {errors.email.type === 'required' && 'Please enter your email address'}
                </span>
              )}
            </div>
            <div>
              <Button
                className="w-full h-full mt-2 lg:w-auto lg:mt-0"
                data-testid="submit-button"
                id="news-button"
                type="submit"
                variant={EButtonColorVariant.Primary}
                label={buttonText}
              />
            </div>
          </form>
          <p className="mt-2 text-gray-500 text-medium autoType200">
            {intl.formatMessage({
              defaultMessage: 'We care about the protection of your data.',
              id: 'Pslzvq',
            })}{' '}
            <Link href="/privacy-policy">
              <span className="text-medium autoType200">
                {intl.formatMessage({
                  defaultMessage: 'Read our privacy policy',
                  id: '8aLrwg',
                })}
              </span>
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

const trackSignup = (source: string) => {
  trackEvent({
    event: 'Growth : Signup : Newsletter sign up',
    source,
  });
};

export default NewsLetter;
